import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  TextField,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';
import { addCampaignDetail, deleteCampaignDetail, editCampaignDetail } from '../../../../../api/endpoints/cmsContent';
import type { CampaignDetail } from '../../../../../api/types/cmsContent';
import { ApiErrorAlert } from '../../../../../components/ApiErrorAlert';
import { ConfirmDialog } from '../../../../../components/ConfirmDialog';

const detailSchema = z.object({
  keywords: z.string().optional(),
  description: z.string().optional(),
  content: z.string().optional(),
});

type DetailFormValues = z.infer<typeof detailSchema>;

const emptyDetailValues: DetailFormValues = { keywords: '', description: '', content: '' };

/** Nested Add/Edit/Delete for `CampaignDetail` rows scoped to one `Campaign` (wire object
 *  `AirCampaign`/`AirCampaignDetail`). This resource has NO standalone list route (confirmed
 *  via the full swagger `/Cms/*` path scan — see api/types/cmsContent.ts's file header), so it
 *  only exists as a child table inside the parent Campaign's edit dialog — never as its own
 *  top-level list page/route. Same design as the sibling
 *  ../../destination/components/DestinationDetailsSection.tsx — keeps its own local copy of
 *  the rows (seeded from the parent's already-fetched `airCampaignDetails`, the ONLY way to
 *  read them) and patches it from each mutation's response, plus invalidates
 *  `['cms','content','campaigns']` so a reopened dialog / the list page reflects the change
 *  afterward. */
export function CampaignDetailsSection({
  campaignId,
  initialDetails,
}: {
  campaignId: number;
  initialDetails: CampaignDetail[];
}) {
  const [details, setDetails] = useState<CampaignDetail[]>(initialDetails);
  useEffect(() => setDetails(initialDetails), [initialDetails]);

  const [formOpen, setFormOpen] = useState(false);
  const [editingDetail, setEditingDetail] = useState<CampaignDetail | null>(null);
  const [deletingDetail, setDeletingDetail] = useState<CampaignDetail | null>(null);
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  const { handleSubmit, register, reset } = useForm<DetailFormValues>({
    resolver: zodResolver(detailSchema),
    defaultValues: emptyDetailValues,
  });

  useEffect(() => {
    if (formOpen) {
      reset(
        editingDetail
          ? {
              keywords: editingDetail.keywords ?? '',
              description: editingDetail.description ?? '',
              content: editingDetail.content ?? '',
            }
          : emptyDetailValues
      );
    }
  }, [formOpen, editingDetail, reset]);

  const invalidateParent = () => queryClient.invalidateQueries({ queryKey: ['cms', 'content', 'campaigns'] });

  const saveMutation = useMutation({
    mutationFn: (values: DetailFormValues) =>
      editingDetail
        ? editCampaignDetail({ ...values, id: editingDetail.id, campaign_Id: campaignId })
        : addCampaignDetail({ ...values, campaign_Id: campaignId }),
    onSuccess: (resp) => {
      const saved = resp.airCampaignDetails?.[0];
      setDetails((prev) => {
        if (!saved) return prev;
        const exists = prev.some((d) => d.id === saved.id);
        return exists ? prev.map((d) => (d.id === saved.id ? saved : d)) : [...prev, saved];
      });
      invalidateParent();
      enqueueSnackbar(editingDetail ? 'Detay güncellendi.' : 'Detay eklendi.', { variant: 'success' });
      setFormOpen(false);
      setEditingDetail(null);
    },
    onError: () => {
      /* surfaced via ApiErrorAlert below */
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: number) => deleteCampaignDetail(id),
    onSuccess: (_resp, id) => {
      setDetails((prev) => prev.filter((d) => d.id !== id));
      invalidateParent();
      enqueueSnackbar('Detay silindi.', { variant: 'success' });
      setDeletingDetail(null);
    },
    onError: (err) => enqueueSnackbar(err instanceof Error ? err.message : 'İşlem başarısız.', { variant: 'error' }),
  });

  const submit = handleSubmit((values) => saveMutation.mutate(values));

  return (
    <Box sx={{ mt: 1 }}>
      <Stack direction="row" sx={{ justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
        <Typography variant="subtitle2">Detay Satırları</Typography>
        <Button
          size="small"
          startIcon={<AddIcon />}
          onClick={() => {
            setEditingDetail(null);
            setFormOpen(true);
          }}
        >
          Detay Ekle
        </Button>
      </Stack>

      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Anahtar Kelimeler</TableCell>
              <TableCell>Açıklama</TableCell>
              <TableCell align="right" />
            </TableRow>
          </TableHead>
          <TableBody>
            {details.length === 0 && (
              <TableRow>
                <TableCell colSpan={3}>
                  <Typography variant="body2" color="text.secondary" sx={{ py: 1, textAlign: 'center' }}>
                    Detay satırı yok.
                  </Typography>
                </TableCell>
              </TableRow>
            )}
            {details.map((row) => (
              <TableRow key={row.id} hover>
                <TableCell>{row.keywords ?? '—'}</TableCell>
                <TableCell>{row.description ?? '—'}</TableCell>
                <TableCell align="right">
                  <IconButton
                    size="small"
                    onClick={() => {
                      setEditingDetail(row);
                      setFormOpen(true);
                    }}
                    aria-label="Düzenle"
                  >
                    <EditIcon fontSize="small" />
                  </IconButton>
                  <IconButton size="small" onClick={() => setDeletingDetail(row)} aria-label="Sil">
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={formOpen} onClose={() => !saveMutation.isPending && setFormOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editingDetail ? 'Detayı Güncelle' : 'Yeni Detay'}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField fullWidth size="small" label="Anahtar Kelimeler" {...register('keywords')} />
            <TextField fullWidth size="small" multiline rows={2} label="Açıklama" {...register('description')} />
            <TextField fullWidth size="small" multiline rows={4} label="İçerik" {...register('content')} />
            <ApiErrorAlert error={saveMutation.error} />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setFormOpen(false)} disabled={saveMutation.isPending}>
            Vazgeç
          </Button>
          <Button onClick={submit} variant="contained" disabled={saveMutation.isPending}>
            {saveMutation.isPending ? 'Kaydediliyor…' : 'Kaydet'}
          </Button>
        </DialogActions>
      </Dialog>

      <ConfirmDialog
        open={!!deletingDetail}
        title="Detayı sil"
        description="Bu detay satırını silmek istediğinize emin misiniz?"
        loading={deleteMutation.isPending}
        onConfirm={() => deletingDetail && deleteMutation.mutate(deletingDetail.id)}
        onCancel={() => setDeletingDetail(null)}
      />
    </Box>
  );
}
